import React, {useCallback, type JSX} from 'react';
import {styled} from '@mui/material/styles';
import {
  type FilePreview,
  type UploadedFile,
} from '../../hooks/useUploadRecords';
import {FileViewer} from './fileViewer/fileViewer';
import {ViewerScreenError} from './fileViewer/viewerScreenError';
import {ViewerScreenLoading} from './fileViewer/viewerScreenLoading';
import {ViewerScreenMessage} from './fileViewer/viewerScreenMessage';
import {FileItem} from './fileListItem';

type Props = {
  filesForPreview: UploadedFile[];
  onSelectFileForPreview: (data: UploadedFile) => void;
  selectedFileForPreview: UploadedFile | undefined;
  onDeleteFile: (id: string) => void;
  onRetryUpload: (data: UploadedFile) => Promise<void>;
  filePreviewIsLoading: boolean;
  filePreviewHasError: boolean;
  filePreviewData: FilePreview;
};

const StyledUploadMedicalRecordsDataViewerWrapper = styled('div')(
  ({theme}) => ({
    width: '100%',
    flexGrow: '1',
    marginTop: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
    minHeight: '200px',
  })
);

const StyledFilesListWrapper = styled('div')(({theme}) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  width: '520px',
  overflowY: 'auto',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  '& > div:last-of-type': {
    marginBottom: 0,
  },
}));

const StyledFilesListOverflowWrapper = styled('div')(({theme}) => ({
  flex: '1 1 auto',
  overflowY: 'auto',
  height: 0,
  padding: theme.spacing(1),
}));

const StyledFilesPreviewWrapper = styled('div')(({theme}) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  flexGrow: 1,
  overflow: 'hidden',
}));

function UploadMedicalRecordsDataViewer({
  filesForPreview,
  onDeleteFile,
  onSelectFileForPreview,
  onRetryUpload,
  selectedFileForPreview,
  filePreviewIsLoading,
  filePreviewHasError,
  filePreviewData,
}: Readonly<Props>): JSX.Element {
  const onRetry = useCallback(
    async () => selectedFileForPreview && onRetryUpload(selectedFileForPreview),
    [onRetryUpload, selectedFileForPreview]
  );

  return (
    <StyledUploadMedicalRecordsDataViewerWrapper>
      <StyledFilesListWrapper>
        <StyledFilesListOverflowWrapper>
          {filesForPreview.map(data => (
            <FilePreviewItem
              key={data.id}
              data={data}
              selectedFileForPreview={selectedFileForPreview}
              onSelectFileForPreview={onSelectFileForPreview}
              onDeleteFile={onDeleteFile}
              onRetryUpload={onRetryUpload}
            />
          ))}
        </StyledFilesListOverflowWrapper>
      </StyledFilesListWrapper>
      <StyledFilesPreviewWrapper>
        {selectedFileForPreview ? null : (
          <ViewerScreenMessage
            title="Secure Viewer for Personal Health Data"
            description="Select file on the left panel to preview"
          />
        )}
        {selectedFileForPreview?.isUploading ? (
          <ViewerScreenLoading message="Loading File" />
        ) : null}
        {selectedFileForPreview?.hasError ? (
          <ViewerScreenError
            title="Loading Failed"
            description="Something went wrong during file loading"
            onRetry={onRetry}
          />
        ) : null}
        {selectedFileForPreview?.isUploaded ? (
          <FileViewer
            filePreviewIsLoading={filePreviewIsLoading}
            filePreviewHasError={filePreviewHasError}
            filePreviewType={filePreviewData?.type}
            filePreviewData={filePreviewData?.data}
          />
        ) : null}
      </StyledFilesPreviewWrapper>
    </StyledUploadMedicalRecordsDataViewerWrapper>
  );
}

type FilePreviewItemProps = {
  data: UploadedFile;
  selectedFileForPreview: UploadedFile | undefined;
  onSelectFileForPreview: (data: UploadedFile) => void;
  onDeleteFile: (id: string) => void;
  onRetryUpload: (data: UploadedFile) => Promise<void>;
};

function FilePreviewItem({
  data,
  selectedFileForPreview,
  onSelectFileForPreview,
  onDeleteFile,
  onRetryUpload,
}: Readonly<FilePreviewItemProps>): JSX.Element {
  const onSelect = useCallback(() => {
    onSelectFileForPreview(data);
  }, [data, onSelectFileForPreview]);
  const onDelete = useCallback(() => {
    onDeleteFile(data.id);
  }, [data.id, onDeleteFile]);
  const onRetry = useCallback(
    async () => onRetryUpload(data),
    [data, onRetryUpload]
  );
  return (
    <FileItem
      key={data.id}
      file={data.file}
      hasError={data.hasError}
      isUploaded={data.isUploaded}
      isLoading={data.isUploading}
      isSelected={data.id === selectedFileForPreview?.id}
      onSelect={onSelect}
      onDelete={onDelete}
      onRetry={onRetry}
      testId={`files-list-${data.file.name}`}
    />
  );
}

export default UploadMedicalRecordsDataViewer;
